'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react';

import {
  search as searchService,
  SearchRequest,
  SearchResponse,
} from '@scorenco/core';

interface ElasticSearchContextType {
  query: string;
  hits: SearchResponse['hits'];
  total: number;
  loading: boolean;
  search: (params: SearchRequest) => Promise<void>;
  refine: (query: string) => Promise<void>;
  clear: () => void;
  // keep filters for compatibility if needed, but managing them in search params is better
}

const ElasticSearchContext = createContext<
  ElasticSearchContextType | undefined
>(undefined);

export const useElasticSearch = () => {
  const context = useContext(ElasticSearchContext);
  if (!context) {
    throw new Error(
      'useElasticSearch must be used within an ElasticSearchProvider'
    );
  }
  return context;
};

export const ElasticSearchProvider = ({
  children,
  defaultFilters = {},
}: {
  children: ReactNode;
  defaultFilters?: Partial<SearchRequest>;
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse>({
    hits: [],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [lastParams, setLastParams] = useState<Partial<SearchRequest>>({});
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const search = useCallback(
    async (params: SearchRequest) => {
      setQuery(params.term);
      setLastParams(params);

      // On clean le timeout précédent
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // On lance le timeout pour le dédoublonnage
      debounceRef.current = setTimeout(async () => {
        setLoading(true);

        // Merge default filters (like type: 'club') with current request
        const mergedParams = { ...defaultFilters, ...params };

        try {
          const response = await searchService(mergedParams);
          setResults(response);
        } catch (error) {
          console.error('Search error:', error);
          setResults({ hits: [], total: 0 });
        } finally {
          setLoading(false);
        }
      }, 300); // 300ms de délai
    },
    [defaultFilters]
  );

  const refine = useCallback(
    async (newQuery: string) => {
      // Re-use last params but update term
      const params: SearchRequest = {
        term: newQuery,
        size: 20, // Default size
        ...lastParams,
        ...defaultFilters,
      } as SearchRequest;

      await search(params);
    },
    [lastParams, defaultFilters, search]
  );

  const clear = useCallback(() => {
    setQuery('');
    setResults({ hits: [], total: 0 });
  }, []);

  return (
    <ElasticSearchContext.Provider
      value={{
        query,
        hits: results.hits,
        total: results.total,
        loading,
        search,
        refine,
        clear,
      }}
    >
      {children}
    </ElasticSearchContext.Provider>
  );
};
